import { useEffect } from "react";
import "./App.css";
import { Toaster } from "react-hot-toast";
import MixpanelService from "./mixpanel";
import KolegaCode from "./components/KolegaCode";
import { DarkModeProvider } from "./components/DarkModeContext";
import { useAuth, AuthProvider } from "./contexts/AuthContext";
import { useDemoMode } from "./hooks/useDemoMode";
import { useAuthenticatedApi } from "./hooks/useAuthenticatedApi";
import { NotificationProvider } from "./contexts/NotificationContext";
import { TourProvider } from "./components/tour";
import { HelpButton } from "./components/tour";
import { SettingsModalProvider } from "./contexts/SettingsModalContext";
import SettingsModals from "./components/modals/SettingsModals";
import { useUserActivity } from "./hooks/useUserActivity";
import { usePaddleInit } from "./hooks/usePaddleInit";
import { useSandboxStore } from "./store/sandboxStore";
import PaymentRedirect from "./components/payment/PaymentRedirect";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
  Outlet,
} from "react-router-dom";
import SecurityOverview from "./components/SecurityOverview";
import ProjectHome from "./components/ProjectHome";
import ThreadInterface from "./components/ThreadInterface";

import DeletedProjectsPage from "./pages/DeletedProjectsPage";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import SubscriptionRequiredRoute from "./components/auth/SubscriptionRequiredRoute";
import HomePage from "./pages/HomePage";
import SubscriptionRequiredPage from "./pages/SubscriptionRequiredPage";
import Header from "./components/common/Header";
import SubscriptionBanner from "./components/common/SubscriptionBanner";
import ImpersonationBanner from "./components/admin/ImpersonationBanner";
import AdminPage from "./pages/AdminPage";
import { initSentry } from "./config/sentry";
import DemoHandler from "./components/demo/DemoHandler";
import DemoDisableOverlay from "./components/common/DemoDisableOverlay";
import PricingPage from "./pages/public/PricingPage";
import TermsPage from "./pages/public/TermsPage";
import PrivacyPage from "./pages/public/PrivacyPage";
import RefundPolicyPage from "./pages/public/RefundPolicyPage";
import ContactPage from "./pages/public/ContactPage";


const ProjectWorkspaceLayout = () => {
  const { workspaceId } = useParams<{ workspaceId: string }>();

  if (!workspaceId) {
    console.error("Workspace ID is missing from URL");
    return <Navigate to="/select-project" replace />;
  }

  return <Outlet context={{ workspaceId }} />;
};

const ProjectDashboard = () => {
  const { workspaceId } = useParams<{ workspaceId: string }>();

  if (!workspaceId) {
    console.error("Workspace ID is missing from URL");
    return <Navigate to="/select-project" replace />;
  }

  return <ProjectHome workspaceId={workspaceId} />;
};

const ProjectThread = () => {
  const { workspaceId, threadId } = useParams<{ workspaceId: string, threadId: string }>();

  if (!workspaceId || !threadId) {
    console.error("Workspace ID or Thread ID is missing from URL");
    return <Navigate to="/select-project" replace />;
  }

  return <ThreadInterface workspaceId={workspaceId} threadId={threadId} />;
};

const ProjectSecurity = () => {
  const { workspaceId } = useParams<{ workspaceId: string }>();

  if (!workspaceId) {
    console.error("Workspace ID is missing from URL");
    return <Navigate to="/select-project" replace />;
  }

  return <SecurityOverview />;
};

function AppContent() {
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const { isDemoMode, demoAccessKey, detectAndStoreDemoFromURL } = useDemoMode();
  const authenticatedApi = useAuthenticatedApi();

  // Initialize user activity tracking (idle detection, cross-tab sync)
  const { isIdle } = useUserActivity();
  const setUserIdleState = useSandboxStore(state => state.setUserIdleState);

  // Initialize Paddle.js (when the active provider is Paddle) and auto-open
  // Paddle-initiated `?_ptxn=` checkout links (dunning / update payment method).
  usePaddleInit();

  // Sync idle state to sandbox store for sleep functionality
  useEffect(() => {
    setUserIdleState(isIdle);
  }, [isIdle, setUserIdleState]);
  
  // Detect and store demo session info on app load or URL changes
  useEffect(() => {
    detectAndStoreDemoFromURL();
  }, [location.pathname, location.search, detectAndStoreDemoFromURL]);
  
  // Check if demo access is still valid when in demo mode
  useEffect(() => {
    if (isDemoMode && demoAccessKey) {
      // Extract workspace ID from URL path
      const pathParts = location.pathname.split('/');
      const workspaceIndex = pathParts.findIndex(part => part === 'workspaces' || part === 'project');
      const workspaceId = workspaceIndex !== -1 && pathParts[workspaceIndex + 1] 
        ? pathParts[workspaceIndex + 1] 
        : null;

      if (workspaceId) {
        // Use the new demo validation endpoint
        const validateDemoAccess = async () => {
          try {
            await authenticatedApi.get(`/demo/validate?demo_access_key=${demoAccessKey}&workspace_id=${workspaceId}`);
            // If we get here without an error, validation succeeded
          } catch (error) {
            console.log('Demo access validation failed, cleaning up and redirecting to homepage');
            console.error('Demo validation error:', error);
            // Clean up localStorage for demo session
            localStorage.removeItem('kolega_demo_session');
            window.location.href = '/';
          }
        };
        validateDemoAccess();
      }
    }
  }, [isDemoMode, demoAccessKey, location.pathname, authenticatedApi]);
  
  // Track page view on route changes
  useEffect(() => {
    // Track page view
    MixpanelService.trackPageView(location.pathname, {
      loggedIn: isAuthenticated || isDemoMode,
      userId: user?.id,
      userEmail: user?.email,
      isDemoMode: isDemoMode,
    });
  }, [location.pathname, isAuthenticated, user?.id, user?.email, isDemoMode]);

  // Determine current page name for help system
  const getCurrentPageName = () => {
    const path = location.pathname;
    if (path === '/select-project') return 'projectSelection';
    if (path === '/new-project') return 'newProject';
    if (path.startsWith('/project/') && path.endsWith('/code')) return 'code';
    if (path.startsWith('/project/') && path.includes('/thread/')) return 'threadInterface';
    if (path.startsWith('/project/') && !path.includes('/')) return 'projectHome';
    if (path === '/deleted-projects') return 'deletedProjects';
    if (path.startsWith('/admin')) return 'admin';
    if (path.startsWith('/account')) return 'account';
    return 'default';
  };

  // Show header for authenticated users or demo mode (homepage has its own header)
  const showHeader = (isAuthenticated || isDemoMode) && location.pathname !== '/';

  useEffect(() => {
    const currentPath = window.location.pathname;
    if (currentPath === '/') {
      localStorage.removeItem('projectPath');
      localStorage.removeItem('projectPort');
    }
  }, []);

  return (
    <>
    <DemoDisableOverlay tooltipMessage="Sign up to use Kolega">
      <div className="flex flex-col h-screen bg-ui-background-primary">
        {showHeader && <Header />}
        {/* Read-only subscription banner */}
        {showHeader && <SubscriptionBanner />}
        <ImpersonationBanner />
        <div className={`${showHeader ? 'flex-1 overflow-auto' : 'h-screen'}`}>
        <Routes>
          {/* Public routes - homepage for login/signup */}
          <Route path="/" element={<HomePage />} />

          {/* Legacy login route - redirect to homepage */}
          <Route path="/login" element={<Navigate to="/" replace />} />

          {/* Public storefront (pricing + legal) — required for Paddle's website review,
              gated via the env flag. When off, these paths fall through to the catch-all
              and redirect to "/". */}
          {process.env.REACT_APP_SHOW_PUBLIC_PAGES === 'true' && (
            <>
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/refund-policy" element={<RefundPolicyPage />} />
              <Route path="/contact" element={<ContactPage />} />
            </>
          )}

          {/* Subscription required page - needs auth but not subscription */}
          <Route path="/subscription-required" element={
            <ProtectedRoute>
              <SubscriptionRequiredPage />
            </ProtectedRoute>
          } />


          {/* Admin routes - require auth but NOT subscription (handled in SubscriptionRequiredRoute) */}
          <Route path="/admin/*" element={
            <SubscriptionRequiredRoute>
              <AdminPage />
            </SubscriptionRequiredRoute>
          } />

          {/* All other routes require subscription */}
          <Route
            path="/select-project"
            element={
              <SubscriptionRequiredRoute allowWithoutSubscription>
                <KolegaCode />
              </SubscriptionRequiredRoute>
            }
          />

          <Route path="/deleted-projects" element={
            <SubscriptionRequiredRoute>
              <DeletedProjectsPage />
            </SubscriptionRequiredRoute>
          } />

          {/* Workspace routes - support both old /project and new /workspaces patterns */}
          <Route path="/project/:workspaceId" element={
            <SubscriptionRequiredRoute allowWithoutSubscription>
              <ProjectWorkspaceLayout />
            </SubscriptionRequiredRoute>
          }>
            <Route index element={<ProjectDashboard />} />
            <Route path="thread/:threadId" element={<ProjectThread />} />
            <Route path="security" element={<ProjectSecurity />} />
            <Route path="security/code" element={<ProjectSecurity />} />
            <Route path="security/code/overview" element={<ProjectSecurity />} />
            <Route path="security/code/quality" element={<ProjectSecurity />} />
          </Route>

          {/* Payment redirect route */}
          <Route path="/payments/:encodedData" element={<PaymentRedirect />} />

          {/* Catch-all route - redirect unknown paths to homepage */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </div>
        {/* Global HelpButton - visible on all authenticated pages */}
        {isAuthenticated && <HelpButton pageName={getCurrentPageName()} />}
        
        {/* Settings Modals - accessible from anywhere */}
        <SettingsModals />
      </div>
    </DemoDisableOverlay>
    
    {/* Demo Handler - manages demo-related UI states - OUTSIDE overlay so modal is clickable */}
    <DemoHandler />
    </>
  );
}

function App() {
  useEffect(() => {
    // Initialize Sentry for error tracking and user feedback
    initSentry();
  }, []);

  return (
    <Router>
      <DarkModeProvider>
        <AuthProvider>
          <SettingsModalProvider>
            <NotificationProvider>
              <TourProvider>
                <AppContent />
                <Toaster 
                  position="top-right"
                  reverseOrder={false}
                  gutter={8}
                  containerStyle={{
                    top: 10,
                    right: 10,
                  }}
                  toastOptions={{
                    className: '',
                    duration: 5000,
                    style: {
                      background: 'var(--toast-bg)',
                      color: 'var(--toast-text)',
                      border: '1px solid var(--toast-border)',
                      borderRadius: '0.375rem',
                      boxShadow: 'var(--toast-shadow)',
                      fontSize: '0.875rem',
                      fontWeight: '400',
                      padding: '0.875rem 1rem',
                      minWidth: '250px',
                      maxWidth: '420px',
                      backdropFilter: 'blur(8px)',
                    },
                    success: {
                      style: {
                        background: 'var(--toast-bg)',
                        color: 'var(--toast-text)',
                        border: '1px solid var(--toast-success-accent)',
                        borderLeft: '3px solid var(--toast-success-accent)',
                      },
                      iconTheme: {
                        primary: 'var(--toast-success-accent)',
                        secondary: 'transparent',
                      },
                      duration: 3000,
                    },
                    error: {
                      style: {
                        background: 'var(--toast-bg)',
                        color: 'var(--toast-text)',
                        border: '1px solid var(--toast-error-accent)',
                        borderLeft: '3px solid var(--toast-error-accent)',
                      },
                      iconTheme: {
                        primary: 'var(--toast-error-accent)',
                        secondary: 'transparent',
                      },
                      duration: 5000,
                    },
                    ariaProps: {
                      role: 'status',
                      'aria-live': 'polite',
                    },
                  }}
                />
              </TourProvider>
            </NotificationProvider>
          </SettingsModalProvider>
        </AuthProvider>
      </DarkModeProvider>
    </Router>
  );
}

export default App;
